import "amazon-connect-streams";
import React, { FC, PropsWithChildren, useState, useContext, useEffect, useReducer, useCallback } from "react";
import AppContext from "./AppContextProvider";
import { CallContext } from "./CallContext";
import { getLogger } from "../services/LoggingService";
import { cloneDeep, isEmpty } from "lodash";
import encodeUrl from "encodeurl";
import { URL_TYPE } from "../services/ConfigurationService";

export const CallContextSetting = {
    ADD_CONTACT_TO_DICT: "ADD_CONTACT_TO_DICT",
    REMOVE_CONTACT_FROM_DICT: "REMOVE_CONTACT_FROM_DICT",
    UPDATE_CONTACT_STATE: "UPDATE_CONTACT_STATE",
};
export function contextReducer(state: any, action: any) {
    switch (action.type) {
        case CallContextSetting.ADD_CONTACT_TO_DICT: {
            return {
                ...state,
                contactDict: action.payload,
            };
        }
        case CallContextSetting.REMOVE_CONTACT_FROM_DICT: {
            let newDict = cloneDeep(state.contactDict);
            if (newDict && newDict[action.payload]) {
                delete newDict[action.payload];
                newDict = isEmpty(newDict) ? undefined : newDict;
            }

            return {
                ...state,
                contactDict: newDict,
            };
        }
        case CallContextSetting.UPDATE_CONTACT_STATE: {
            const { contactId, newState } = action.payload;
            if (!state.contactDict || !state.contactDict[contactId]) {
                return {
                    ...state,
                };
            } else {
                return {
                    ...state,
                    contactDict: {
                        ...state.contactDict,
                        [contactId]: {
                            ...state.contactDict[contactId],
                            contactState: newState,
                        },
                    },
                };
            }
        }
        default:
            return state;
    }
}
const CallContextProvider: FC<PropsWithChildren<any>> = (props: PropsWithChildren<any>) => {
    const logger = getLogger("CallContextProvider");

    const [state, dispatch] = useReducer(contextReducer, CallContext);

    const [voiceContact, setVoiceContact] = useState<connect.Contact | undefined>(undefined);
    const [agent, setAgent] = useState<connect.Agent | undefined>(undefined);

    const { config } = useContext(AppContext);

    const setContactConnected = (newContact: connect.Contact | undefined): void => {
        if (!newContact) return;
        if (
            newContact.getType() === connect.ContactType.VOICE ||
            newContact.getType() === connect.ContactType.QUEUE_CALLBACK
        ) {
            setVoiceContact(newContact);
        }
        contactHandler(newContact);
    };

    const addContactToDict = useCallback((contactObj: any) => {
        dispatch({ type: CallContextSetting.ADD_CONTACT_TO_DICT, payload: contactObj });
    }, []);

    const removeContactFromDict = useCallback((contactId: string) => {
        dispatch({ type: CallContextSetting.REMOVE_CONTACT_FROM_DICT, payload: contactId });
    }, []);

    const updateContactState = useCallback((contactId: string, newState: connect.ContactStateType) => {
        dispatch({ type: CallContextSetting.UPDATE_CONTACT_STATE, payload: { contactId, newState } });
    }, []);

    const createContactObj = (
        contactId: string,
        attributes: any,
        ani: string,
        contactType: string,
        contactState: connect.ContactStateType,
        connectionType: connect.ConnectionType | undefined
    ) => {
        logger.debug("CREATE CONTACT OBJ", { contactId, attributes, ani, contactType, contactState, connectionType });
        const workflowURL = encodeUrl(config.getScreenPopURL(URL_TYPE.WORKFLOW, attributes, ani));
        const agentSupportURL = encodeUrl(config.getScreenPopURL(URL_TYPE.AGENT_SUPPORT, attributes, ani));
        const screenPopURL = encodeUrl(config.getScreenPopURL(URL_TYPE.SCREEN_POP, attributes, ani));

        const showWorkflow = attributes.showWorkflow && attributes.showWorkflow.value === "false" ? false : true;
        logger.debug("Show Workflow ", showWorkflow);
        logger.debug("Workflow ", workflowURL);
        logger.debug("Agent URL", agentSupportURL);
        logger.debug("screenPop ", screenPopURL);
        return {
            [contactId]: {
                contactId,
                contactType,
                connectionType,
                contactState,
                workflowURL,
                agentSupportURL,
                screenPopURL,
                showWorkflow,
                attributes,
            },
        };
    };
    const contactHandler = (_contact: connect.Contact) => {
        if (!_contact) return;
        logger.debug("CONTACT:STATE:CONTACT", _contact.getStatus().type, _contact);
        //const ani = _contact.getActiveInitialConnection()?.getEndpoint()?.phoneNumber || "";
        const ani = _contact.getInitialConnection()?.getEndpoint()?.phoneNumber || "";
        const attributes = _contact.getAttributes();
        const contactObj = createContactObj(
            _contact.contactId,
            attributes,
            ani,
            _contact.getType(),
            _contact.getStatus().type,
            _contact.getActiveInitialConnection()?.getType()
        );
        addContactToDict(contactObj);
        _contact.onConnected(() => {
            logger.debug("CONTACT:STATE:CONNECTED", _contact.getStatus().type, _contact);
            const attributes = _contact.getAttributes();
            logger.debug("ATTRIBUTES ", attributes);
            updateContactState(_contact.contactId, _contact.getStatus().type);
            const ani = _contact.getInitialConnection()?.getEndpoint()?.phoneNumber || "";
            const contactObj = createContactObj(
                _contact.contactId,
                attributes,
                ani,
                _contact.getType(),
                _contact.getStatus().type,
                _contact.getActiveInitialConnection()?.getType()
            );
            addContactToDict(contactObj);
        });
        _contact.onEnded(() => {
            logger.debug("CONTACT:STATE:ENDED", _contact.getStatus().type, _contact);
            updateContactState(_contact.contactId, _contact.getStatus().type);
        });
        _contact.onDestroy(() => {
            logger.debug("CONTACT:STATE:DESTROY", _contact);
            setVoiceContact(undefined);
            removeContactFromDict(_contact.contactId);
        });
        _contact.onConnecting(() => {
            logger.debug("CONNECTING:CONNECTING");
            updateContactState(_contact.contactId, _contact.getStatus().type);
        });

        if ([connect.ContactStateType.ENDED, connect.ContactStateType.CONNECTED].includes(_contact.getStatus().type)) {
            logger.debug("CONTACT:STATE:ENDED-CONNECTED", _contact.getStatus().type, _contact);
            const attributes = _contact.getAttributes();
            //const ani = _contact.getActiveInitialConnection()?.getEndpoint()?.phoneNumber || "";
            const ani = _contact.getInitialConnection()?.getEndpoint()?.phoneNumber || "";
            const contactObj = createContactObj(
                _contact.contactId,
                attributes,
                ani,
                _contact.getType(),
                _contact.getStatus().type,
                _contact.getInitialConnection()?.getType()
            );
            addContactToDict(contactObj);
        }
    };

    return (
        <CallContext.Provider
            value={{
                contactDict: state.contactDict,
                contact: voiceContact,
                agent: agent,
                setContact: setContactConnected,
                setAgent: setAgent,
            }}
        >
            {props.children}
        </CallContext.Provider>
    );
};

CallContext.displayName = "CallContext"; // used in dev tools
export { CallContextProvider };
